import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { REQUEST_STATUS } from '../../../../../redux_store';
import Skeleton from '@material-ui/lab/Skeleton';
import { formatNumber } from '../../../utils/formatters';

const useStyles = makeStyles((theme) => ({
  patientCount: {
    borderRadius: 12,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },

  // Regular mode styles
  regularContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(3),
    width: '100%',
  },
  metricSection: {
    flex: 1,
    textAlign: 'center',
  },
  metricValue: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1,
    marginBottom: 2,
    letterSpacing: '-0.5px',
  },
  metricLabel: {
    fontSize: 9,
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  patientsColor: {
    color: theme.colors.blue || '#3B82F6',
  },
  alertsColor: {
    color: '#F59E0B',
  },

  // Comparison mode styles
  comparisonContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    width: '100%',
    alignItems: 'center',
    gap: 12,
    minHeight: 60, // Increased height to prevent text overlap
    padding: '4px 0',
  },
  comparisonColumnHeader: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  comparisonColumn: {
    display: 'flex',
    gap: 20,
  },
  comparisonMetric: {
    textAlign: 'center',
    flex: 1,
  },
  comparisonValue: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.5px',
  },
  comparisonLabel: {
    fontSize: 9,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginTop: 1,
  },
  comparisonHeader: {
    fontSize: 10,
    fontWeight: 600,
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    position: 'absolute',
    top: 4,
  },
  comparisonHeaderLeft: {
    position: 'relative',
  },
  comparisonHeaderRight: {
    position: 'relative',
  },
  baselineColor: {
    color: '#6B7280',
  },
  followupColor: {
    color: theme.colors.blue2 || '#3B82F6',
  },

  divider: {
    width: '1px',
    height: '28px',
    backgroundColor: '#D6E8FF',
  },
}));

const PatientCounter = () => {
  const classes = useStyles();
  const { en: { populationHealth } } = useSelector((state) => state.i18n);
  const {
    totalPatients,
    totalAlerts,
    totalBaselinePatients,
    totalBaselineAlerts,
    totalFollowupPatients,
    totalFollowupAlerts,
    isComparisonMode,
    status,
  } = useSelector((state) => state.populationHealth);

  // Mock data for alerts if not available from state
  const alerts = totalAlerts || '-';
  const baselineAlerts = totalBaselineAlerts || '-';
  const followupAlerts = totalFollowupAlerts || '-';

  if (isComparisonMode) {
    return (
      <div className={classes.patientCount}>
        <div className={classes.comparisonContainer}>
          <div className={classes.comparisonColumnHeader}>
            <div
              className={`${classes.comparisonHeader} ${classes.comparisonHeaderLeft} ${classes.baselineColor}`}>
              BASELINE
            </div>
            <div className={classes.comparisonColumn}>
              <div className={classes.comparisonMetric}>
                <div
                  className={`${classes.comparisonValue} ${classes.patientsColor}`}>
                  {status === REQUEST_STATUS.loading || status === REQUEST_STATUS.idle
                    ? <Skeleton variant="text" width={45} height={20} />
                    : formatNumber(totalBaselinePatients || 0)}
                </div>
                <div className={classes.comparisonLabel}>Patients</div>
              </div>
              <div className={classes.comparisonMetric}>
                <div className={`${classes.comparisonValue} ${classes.alertsColor}`}>
                  {status === REQUEST_STATUS.loading || status === REQUEST_STATUS.idle
                    ? <Skeleton variant="text" width={45} height={20} />
                    : formatNumber(baselineAlerts)}
                </div>
                <div className={classes.comparisonLabel}>Alerts</div>
              </div>
            </div>
          </div>
          <div className={classes.divider} />
          <div className={classes.comparisonColumnHeader}>

            <div className={`${classes.comparisonHeader} ${classes.comparisonHeaderRight} ${classes.baselineColor}`}>
              FOLLOW-UP
            </div>
            <div className={classes.comparisonColumn}>
              <div className={classes.comparisonMetric}>
                <div className={`${classes.comparisonValue} ${classes.patientsColor}`}>
                  {status === REQUEST_STATUS.loading || status === REQUEST_STATUS.idle
                    ? <Skeleton variant="text" width={45} height={20} />
                    : formatNumber(totalFollowupPatients || 0)}
                </div>
                <div className={classes.comparisonLabel}>Patients</div>
              </div>
              <div className={classes.comparisonMetric}>
                <div className={`${classes.comparisonValue} ${classes.alertsColor}`}>
                  {status === REQUEST_STATUS.loading || status === REQUEST_STATUS.idle
                    ? <Skeleton variant="text" width={45} height={20} />
                    : formatNumber(followupAlerts)}
                </div>
                <div className={classes.comparisonLabel}>Alerts</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.patientCount}>
      <div className={classes.regularContainer}>
        <div className={classes.metricSection}>
          <div className={`${classes.metricValue} ${classes.patientsColor}`}>
            {status === REQUEST_STATUS.loading || status === REQUEST_STATUS.idle
              ? <Skeleton variant="text" width={60} height={25} />
              : formatNumber(totalPatients || 1234)}
          </div>
          <div className={classes.metricLabel}>Patients</div>
        </div>
        <div className={classes.divider} />
        <div className={classes.metricSection}>
          <div className={`${classes.metricValue} ${classes.alertsColor}`}>
            {status === REQUEST_STATUS.loading || status === REQUEST_STATUS.idle
              ? <Skeleton variant="text" width={60} height={25} />
              : formatNumber(alerts)}
          </div>
          <div className={classes.metricLabel}>Alerts</div>
        </div>
      </div>
    </div>
  );
};

export default PatientCounter;
